import { PointsAuditEndpoint } from '@api/endpoints';
import { PointsMovementReason, RewardsProgramType } from '@shared/enums';
import { TFunction } from 'i18next';

type ProcessReason = PointsAuditEndpoint.GetProcess.ProcessReason;

export interface ReasonTotal {
  farmersAffected: number;
  ledgerRows: number;
  pointsDelta: number;
  reason: PointsMovementReason;
}

export interface FarmerReasonTotal {
  ledgerRows: number;
  pointsDelta: number;
  reason: PointsMovementReason;
}

export interface TierTotal {
  pointsDelta: number;
  tierName: string | null;
  tierRank: number | null;
}

/** Biggest mover first, by size rather than sign. */
const byAbsoluteDelta = (a: { pointsDelta: number }, b: { pointsDelta: number }) => (
  Math.abs(b.pointsDelta) - Math.abs(a.pointsDelta)
);

/** One line per reason across all farmers. Tier is dropped; it belongs on each farmer's line. */
export const rollUpReasons = (reasons: ProcessReason[]): ReasonTotal[] => {
  const totals = new Map<PointsMovementReason, ReasonTotal & { farmers: Set<string> }>();

  reasons.forEach((row) => {
    const existing = totals.get(row.reason) ?? {
      farmers: new Set<string>(),
      farmersAffected: 0,
      ledgerRows: 0,
      pointsDelta: 0,
      reason: row.reason,
    };
    existing.farmers.add(row.farmerId);
    existing.ledgerRows += row.ledgerRows;
    existing.pointsDelta += row.pointsDelta;
    totals.set(row.reason, existing);
  });

  return [...totals.values()]
    .map(({ farmers, ...total }) => ({ ...total, farmersAffected: farmers.size }))
    .sort(byAbsoluteDelta);
};

/** Usually one; a backdated order across a tier change earns at both rates. */
export const tiersForFarmer = (
  reasons: ProcessReason[],
  farmerId: string,
  programType?: RewardsProgramType,
): TierTotal[] => {
  const byTier = new Map<string, TierTotal>();

  reasons
    .filter((row) => (
      row.farmerId === farmerId
      && row.tierRank !== null
      && (!programType || row.programType === programType)
    ))
    .forEach((row) => {
      const key = String(row.tierRank);
      const existing = byTier.get(key)
        ?? { pointsDelta: 0, tierName: row.tierName, tierRank: row.tierRank };
      existing.pointsDelta += row.pointsDelta;
      byTier.set(key, existing);
    });

  return [...byTier.values()].sort(byAbsoluteDelta);
};

/** Merged across tier, or a farmer spanning a tier change shows each reason twice. */
export const reasonsForFarmer = (
  reasons: ProcessReason[],
  farmerId: string,
  programType?: RewardsProgramType,
): FarmerReasonTotal[] => {
  const byReason = new Map<PointsMovementReason, FarmerReasonTotal>();

  reasons
    .filter((row) => (
      row.farmerId === farmerId && (!programType || row.programType === programType)
    ))
    .forEach((row) => {
      const existing = byReason.get(row.reason)
        ?? { ledgerRows: 0, pointsDelta: 0, reason: row.reason };
      existing.ledgerRows += row.ledgerRows;
      existing.pointsDelta += row.pointsDelta;
      byReason.set(row.reason, existing);
    });

  return [...byReason.values()].sort(byAbsoluteDelta);
};

export const describeMovementReason = (
  reason: PointsMovementReason,
  t: TFunction,
): string => {
  switch (reason) {
    case PointsMovementReason.BaseEarning:
      return t($ => $.pointsAudit.reason.baseEarning);
    case PointsMovementReason.BookingReserve:
      return t($ => $.pointsAudit.reason.bookingReserve);
    case PointsMovementReason.Correction:
      return t($ => $.pointsAudit.reason.correction);
    case PointsMovementReason.Expiration:
      return t($ => $.pointsAudit.reason.expiration);
    case PointsMovementReason.Manual:
      return t($ => $.pointsAudit.reason.manual);
    case PointsMovementReason.Promotion:
      return t($ => $.pointsAudit.reason.promotion);
    case PointsMovementReason.Redemption:
      return t($ => $.pointsAudit.reason.redemption);
    case PointsMovementReason.Refund:
      return t($ => $.pointsAudit.reason.refund);
    case PointsMovementReason.Return:
      return t($ => $.pointsAudit.reason.returned);
    default:
      return reason;
  }
};

export const formatSignedPoints = (points: number): string => (
  points > 0 ? `+${points.toLocaleString()}` : points.toLocaleString()
);
